import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle2 } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { toast } from "sonner";

export default function ResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error(t("password_too_short") || "Min 8 characters"); return; }
    if (pw !== pw2) { toast.error(t("passwords_dont_match") || "Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      toast.error(errorMessage(err, t));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0E0E12] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <img src="/logo-mark.png" alt="Vixelforge" className="w-8 h-8 shrink-0" />
            <span className="font-black text-xl tracking-tight">Vixelforge</span>
          </Link>
          <LanguageSwitcher />
        </div>

        {!token ? (
          <div className="rounded-lg border border-red-500/30 bg-red-500/[0.06] p-6 text-center">
            <p className="text-red-200">{t("invalid_reset_link") || "Invalid or missing reset link"}</p>
          </div>
        ) : done ? (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] p-8 text-center" data-testid="reset-done">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
            <h1 className="font-black text-2xl mb-2">{t("password_updated") || "Password updated"}</h1>
            <p className="text-zinc-400 text-sm">{t("password_updated_msg") || "Redirecting you to sign in…"}</p>
          </div>
        ) : (
          <>
            <h1 className="font-black text-3xl md:text-4xl tracking-tight mb-2">{t("set_new_password") || "Set a new password"}</h1>
            <p className="text-zinc-400 text-sm mb-6">{t("reset_password_hint") || "Pick something with at least 8 characters."}</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("new_password") || "New password"}</Label>
                <Input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)}
                  data-testid="reset-pw-input"
                  className="bg-[#16161D] border-white/10 focus:ring-orange-500 mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("confirm_password") || "Confirm password"}</Label>
                <Input type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)}
                  data-testid="reset-pw2-input"
                  className="bg-[#16161D] border-white/10 focus:ring-orange-500 mt-1.5 h-11" />
              </div>
              <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
                className="w-full h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold">
                {loading ? "..." : (t("update_password") || "Update password")}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
